import React, { useEffect, useState } from 'react';
import { observer } from 'mobx-react';
import { Button, Divider, Radio } from 'antd';
import { ArrowLeftOutlined, BankOutlined, CreditCardOutlined, ShoppingCartOutlined } from '@ant-design/icons';
import { Catcher, LoadingApiByIds, booleanYNText } from '../';
import cx from 'classnames';
import { useProfile, useRouterStore, useTariffsToBuyItem } from '../../hooks';
import {
    getCompaniesChildrenSelect,
    getCountriesSelect,
    getNetworksSelect,
    getOperatorsSelect,
    getTariffToBuyItem,
} from '../../middleware';
import { getTariffPackagesList } from '../../middleware';
import { stringMoney, tAdvancedPlural } from '../../instruments';
import { tB, tF, tI } from '../../translate';
import { AssignTariffModal } from './Components/AssignTariffModal/AssignTatiffModal';
import { TariffItemPackageView } from './Components/TariffItem/TariffItemPackageView';
import { TariffItemAssignedCards } from './Components/TariffItem/TariffsItemAssignedCards';
import CardStyles from '../styles/m_itemCardStyles.less';
import TariffsStyles from './m_styles.less';

export const TariffToBuyItem: React.FC = observer(() => {
    const [isAssignOpen, setIsAssignOpen] = useState(false);
    const [currentView, setCurrentView] = useState<'packageView' | 'cards'>('packageView');
    const companyId = useProfile().companyId;
    const tariff = useTariffsToBuyItem();
    const routerStore = useRouterStore();
    const currentRouteId = routerStore.routerState.params.id;

    const { companyName, cardsCount, tariffPackages, price, outOfBundle, productId, planType, cards } = tariff;

    const goTo = (event: React.MouseEvent): void => {
        if (!(event.ctrlKey || event.metaKey || event.altKey || event.shiftKey)) {
            event.preventDefault();

            routerStore.goTo('TariffsList').then();
        }
    };

    const openAssignStepper = () => {
        setIsAssignOpen(true);
    };

    const closeAssignStepper = () => {
        setIsAssignOpen(false);
    };

    useEffect(() => {
        if (currentRouteId && companyId) {
            getTariffToBuyItem(currentRouteId).then();
            getTariffPackagesList().then();
            getCompaniesChildrenSelect(companyId).then();
            getOperatorsSelect().then();
            getNetworksSelect().then();
            getCountriesSelect().then();
        }
    }, [currentRouteId, companyId]);

    const views = {
        packageView: <TariffItemPackageView tariffPackages={tariffPackages} />,
        // assigned: <TariffItemAssignedCards cards={cards} />,
        cards: <TariffItemAssignedCards cards={cards} />,
    };

    return (
        <Catcher>
            <div className={cx(CardStyles.main, TariffsStyles.container)}>
                <div className={TariffsStyles.tariffItemContainer}>
                    <Button onClick={(e) => goTo(e)} type={'ghost'}>
                        <ArrowLeftOutlined /> {tB('Back To Tariffs')}
                    </Button>

                    <div className={TariffsStyles.tariffItemHeaderContainer}>
                        <div>
                            <h1>{tariff.name}</h1>
                            <p>{tariff.description}</p>
                        </div>
                        <div className={TariffsStyles.tariffItemButtonsContainer}>
                            <Button type={'primary'} onClick={openAssignStepper}>
                                <ShoppingCartOutlined /> {tB('Buy Tariff')}
                            </Button>
                        </div>
                    </div>
                    <Divider />
                    <div className={TariffsStyles.tariffItemStartsContainer}>
                        <div className={TariffsStyles.tariffItemStats}>
                            <div className={TariffsStyles.tariffItemStatsTitle}>{tF('Price')}</div>
                            <div className={TariffsStyles.tariffItemStatsInfo}>{stringMoney(price, 'EUR')}</div>
                        </div>
                        <div className={TariffsStyles.tariffItemStats}>
                            <div className={TariffsStyles.tariffItemStatsTitle}>{tI('Out of Bundle')}</div>
                            <div className={TariffsStyles.tariffItemStatsInfo}>{booleanYNText(outOfBundle)}</div>
                        </div>
                        {productId && (
                            <div className={TariffsStyles.tariffItemStats}>
                                <div className={TariffsStyles.tariffItemStatsTitle}>{tF('Product ID')}</div>
                                <div className={TariffsStyles.tariffItemStatsInfo}>{productId}</div>
                            </div>
                        )}
                        {planType && (
                            <div className={TariffsStyles.tariffItemStats}>
                                <div className={TariffsStyles.tariffItemStatsTitle}>{tF('Plan Type')}</div>
                                <div className={TariffsStyles.tariffItemStatsInfo}>{planType}</div>
                            </div>
                        )}
                        <div className={TariffsStyles.tariffItemStats}>
                            <div className={TariffsStyles.tariffItemStatsTitle}>{tF('Created by')}</div>
                            <div className={TariffsStyles.tariffItemStatsInfo}>
                                <BankOutlined />
                                {companyName}
                            </div>
                        </div>
                        {/*<div className={TariffsStyles.tariffItemStats}>*/}
                        {/*    <div className={TariffsStyles.tariffItemStatsTitle}>Assigned Companies</div>*/}
                        {/*    <div className={TariffsStyles.tariffItemStatsInfo}>*/}
                        {/*        <UsergroupAddOutlined />*/}
                        {/*        {companies?.length} companies*/}
                        {/*    </div>*/}
                        {/*</div>*/}
                        <div className={TariffsStyles.tariffItemStats}>
                            <div className={TariffsStyles.tariffItemStatsTitle}>{tF('Active Cards')}</div>
                            <div className={TariffsStyles.tariffItemStatsInfo}>
                                <CreditCardOutlined />
                                {tAdvancedPlural('card', cardsCount)}
                            </div>
                        </div>
                    </div>
                    <div>
                        <Radio.Group
                            defaultValue="packageView"
                            buttonStyle="solid"
                            onChange={(e) => setCurrentView(e.target.value)}
                        >
                            <Radio.Button value="packageView">{tF('Packages')}</Radio.Button>
                            {/*<Radio.Button value="assigned">Assigned Companies</Radio.Button>*/}
                            <Radio.Button value="cards">{tF('Cards Using Tariff')}</Radio.Button>
                        </Radio.Group>
                    </div>
                    {views[currentView]}
                    <AssignTariffModal isOpen={isAssignOpen} closeStepper={closeAssignStepper} tariff={tariff} />
                    <LoadingApiByIds idSpinners={['GET_TARIFF']} />
                </div>
            </div>
        </Catcher>
    );
});
